
import { useCallback, useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';

interface AbandonedRow {
  paymentRequestId: string;
  lupsRequestId: string | null;
  advanceRequestId: string;
  installmentDetailId: string;
  companyId: string;
  companyName: string | null;
  companyTaxId: string | null;
  monto: number;
  fechaObjetivo: string;
  abandonmentDate: string;
  abandonedAt: string;
  resolutionNote: string | null;
  promissoryNoteKey: string | null;
}

interface Paginated<T> {
  data: T[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

const formatCLP = (n: number) => n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};
const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function AbandonedInstallmentsTable() {
  const [response, setResponse] = useState<Paginated<AbandonedRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [withNote, setWithNote] = useState<'all' | 'true' | 'false'>('all');
  const [editingRow, setEditingRow] = useState<AbandonedRow | null>(null);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAdminEndpointUrl('abandonedInstallments', 'list');
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', '20');
      if (fechaDesde) qs.set('fechaDesde', fechaDesde);
      if (fechaHasta) qs.set('fechaHasta', fechaHasta);
      if (withNote !== 'all') qs.set('withNote', withNote);
      const data = await adminHttpClient.get<Paginated<AbandonedRow>>(`${base}?${qs.toString()}`);
      setResponse(data);
    } catch (err) {
      console.error('Error cargando cuotas abandonadas', err);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [page, fechaDesde, fechaHasta, withNote]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <select
            value={withNote}
            onChange={(e) => {
              setWithNote(e.target.value as 'all' | 'true' | 'false');
              setPage(1);
            }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="all">Todas</option>
            <option value="false">Sin nota de seguimiento</option>
            <option value="true">Con nota de seguimiento</option>
          </select>
          <input
            type="date"
            value={fechaDesde}
            max={fechaHasta || undefined}
            onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }}
            placeholder="Desde"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={fechaHasta}
            min={fechaDesde || undefined}
            onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }}
            placeholder="Hasta"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex items-center text-xs text-muted-foreground">
            {response ? `${response.pagination.total} cuota(s) abandonada(s)` : '—'}
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-3 px-4">Comercio</th>
              <th className="py-3 px-4">Adelanto / Cuota</th>
              <th className="py-3 px-4 text-right">Monto</th>
              <th className="py-3 px-4">Fecha objetivo</th>
              <th className="py-3 px-4">Fecha de abandono</th>
              <th className="py-3 px-4">Abandonada el</th>
              <th className="py-3 px-4">Nota</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>
            )}
            {!loading && (response?.data.length ?? 0) === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No hay cuotas abandonadas con los filtros actuales.</td></tr>
            )}
            {!loading && response?.data.map((row) => (
              <tr key={row.paymentRequestId} className="border-t border-border">
                <td className="py-3 px-4">
                  <div className="font-medium text-foreground">{row.companyName ?? '—'}</div>
                  <div className="text-xs text-muted-foreground font-mono">{row.companyTaxId ?? row.companyId}</div>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-muted-foreground">
                  <div>{row.advanceRequestId}</div>
                  <div className="opacity-60">{row.installmentDetailId}</div>
                </td>
                <td className="py-3 px-4 text-right font-medium">{formatCLP(row.monto)}</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">{formatDate(row.fechaObjetivo)}</td>
                <td className="py-3 px-4 text-xs text-muted-foreground">{formatDate(row.abandonmentDate)}</td>
                <td className="py-3 px-4 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground/80">
                    <span className="material-symbols-outlined text-[12px]">block</span>
                    {formatDateTime(row.abandonedAt)}
                  </span>
                </td>
                <td className="py-3 px-4 text-xs text-muted-foreground max-w-sm truncate">{row.resolutionNote ?? '—'}</td>
                <td className="py-3 px-4">
                  <Button variant="outline" size="sm" onClick={() => setEditingRow(row)}>
                    {row.resolutionNote ? 'Editar' : 'Registrar nota'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {response && response.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground">Página {response.pagination.page} de {response.pagination.totalPages}</p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-md border px-3 py-1 text-sm disabled:opacity-40">Anterior</button>
            <button disabled={page >= response.pagination.totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-md border px-3 py-1 text-sm disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}

      {editingRow && (
        <EditNoteModal row={editingRow} onClose={() => setEditingRow(null)} onSaved={async () => { setEditingRow(null); await fetchPage(); }} />
      )}
    </div>
  );
}

function EditNoteModal({ row, onClose, onSaved }: { row: AbandonedRow; onClose: () => void; onSaved: () => Promise<void> }) {
  const [note, setNote] = useState(row.resolutionNote ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await adminHttpClient.put(getAdminEndpointUrl('abandonedInstallments', 'updateNote', { paymentRequestId: row.paymentRequestId }), { note });
      await onSaved();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-background p-6 shadow-xl">
        <h3 className="text-lg font-semibold">Nota de seguimiento — cuota abandonada</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Comercio: <strong>{row.companyName ?? row.companyId}</strong> · Monto: {formatCLP(row.monto)} · Abandonada: {formatDateTime(row.abandonedAt)}
        </p>
        <form onSubmit={handleSave} className="mt-4 space-y-3">
          <label className="block text-sm">
            Nota
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" rows={6} placeholder="Ej. PAC manual coordinado con el banco el 12/05, recupero parcial $45.000 ingresado vía ajuste MEL manual, aceptar pérdida…" required />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Guardando…' : 'Guardar nota'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
